import React, { useState, useEffect } from "react";
import { Form, Button, Message } from "semantic-ui-react";
import { useFormik } from "formik";
import { User, Role, Company, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { initialValues, validationSchema } from "./InviteUserForm.form";

const userController = new User();
const roleController = new Role();
const companyController = new Company();
const siteController = new Site();

export function InviteUserForm({ close, onReload }) {
  const { accessToken, user: { role, company } } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [listRoles, setListRoles] = useState([]);
  const [listCompanies, setListCompanies] = useState([]);
  const [listSites, setListSites] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    roleController.getRoles(accessToken, true).then(setListRoles);

    if (isMaster(role)) {
      companyController.getCompanies(accessToken, true).then(setListCompanies);
    } else if (isAdmin(role) && company) {
      siteController.getSitesByCompany(accessToken, company, true).then(setListSites);
    }
  }, []);

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        setError("");
        setSuccessMsg("");

        const data = {
          email: formValue.email,
          role: formValue.role,
          sites: formValue.sites,
          company: isMaster(role) ? formValue.company || undefined : undefined,
        };

        await userController.inviteUser(accessToken, data);
        setSuccessMsg(`${t("invitation_sent")} ${formValue.email}`);
        formik.resetForm();

        setTimeout(() => {
          onReload && onReload();
          close && close();
        }, 1800);
      } catch (err) {
        setError(err.msg || t("error_sending_invitation"));
      }
    },
  });

  // Cuando el master cambia de empresa, recargar sitios
  useEffect(() => {
    if (isMaster(role) && formik.values.company) {
      siteController
        .getSitesByCompany(accessToken, formik.values.company, true)
        .then(setListSites);
    }
  }, [formik.values.company]);

  return (
    <Form onSubmit={formik.handleSubmit} style={{ padding: "1rem" }}>
      {successMsg && <Message positive content={successMsg} />}
      {error && <Message negative content={error} />}

      <Form.Input
        label={t("email")}
        name="email"
        type="email"
        placeholder={t("email")}
        onChange={formik.handleChange}
        value={formik.values.email}
        error={formik.errors.email}
        required
      />

      <Form.Dropdown
        label={t("role")}
        placeholder={t("select")}
        options={listRoles.map((r) => ({ key: r._id, text: r.name, value: r._id }))}
        selection
        onChange={(_, data) => formik.setFieldValue("role", data.value)}
        value={formik.values.role}
        error={!!formik.errors.role}
        required
      />

      {isMaster(role) && (
        <Form.Dropdown
          label={t("company")}
          placeholder={t("select")}
          options={listCompanies.map((c) => ({ key: c._id, text: c.name, value: c._id }))}
          selection
          onChange={(_, data) => {
            formik.setFieldValue("company", data.value);
            formik.setFieldValue("sites", []);
          }}
          value={formik.values.company}
        />
      )}

      <Form.Dropdown
        label={t("site")}
        placeholder={t("select")}
        options={listSites.map((s) => ({ key: s._id, text: s.name, value: s._id }))}
        selection
        multiple
        clearable
        onChange={(_, data) => formik.setFieldValue("sites", data.value)}
        value={formik.values.sites}
      />

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", justifyContent: "center" }}>
        <Button type="submit" primary loading={formik.isSubmitting} style={{ minWidth: "120px" }}>
          {t("invite_user")}
        </Button>
        <Button type="button" secondary onClick={close} style={{ minWidth: "120px" }}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
